import { StatsigClient } from '@statsig/js-client';
import type { StatsigUser } from '@statsig/js-client';
import type { DeviceType } from './getDeviceType';
import type { BrowserName } from './getBrowser';

export type ScreenerSource = 'email' | 'direct' | 'unknown';
export type AbandonStep =
  | 'landing_page'
  | 'permission_prompt'
  | 'questions'
  | 'review'
  | 'submission';

export const SCREENER_STARTED_AT_KEY = 'screenerStartedAt';

interface BaseProps {
  jobId?: string;
  companyId?: string;
  applicantGuid?: string;
  deviceType?: DeviceType;
}

export interface LandingPageViewedProps extends BaseProps {
  source: ScreenerSource;
}

export interface CtaClickedProps extends BaseProps {
  source: ScreenerSource;
}

export interface RecordingStartedProps extends BaseProps {
  questionNumber: number;
}

export interface RecordingCompletedProps extends BaseProps {
  questionNumber: number;
  recordingLengthSeconds: number;
}

export interface RecordingRetakenProps extends BaseProps {
  questionNumber: number;
  attemptNumber: number;
}

export interface ScreenerCompletedProps extends BaseProps {
  totalQuestions: number;
  totalTimeSeconds: number;
}

export interface ScreenerAbandonedProps extends BaseProps {
  lastStep: AbandonStep;
  questionNumber?: number;
  secondsOnPage: number;
}

export interface CameraErrorProps extends BaseProps {
  errorCode?: string;
  errorMessage?: string;
  browser?: BrowserName;
}

export interface UploadErrorProps extends BaseProps {
  questionNumber?: number;
  errorCode?: string;
  errorMessage?: string;
}

export interface PageLoadErrorProps extends BaseProps {
  errorCode?: string;
  errorMessage?: string;
  browser?: BrowserName;
}

export type PermissionResult = 'allow' | 'block' | 'prompt';
export type PermissionType = 'camera' | 'microphone';

export interface PermissionProps extends BaseProps {
  result: PermissionResult;
  permissionType: PermissionType;
}

function getClient(): StatsigClient | null {
  try {
    const client = StatsigClient.instance();
    if (!client) return null;
    return client;
  } catch (error: unknown) {
    return null;
  }
}

function toMetadata(props: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined) continue;
    out[key] = typeof value === 'string' ? value : String(value);
  }
  return out;
}

function safeLog(eventName: string, props: Record<string, unknown>): void {
  try {
    const client = getClient();
    if (!client) return;
    client.logEvent(eventName, undefined, toMetadata(props));
  } catch (error: unknown) {
    console.error(`[Statsig] Failed to log ${eventName}`, error);
  }
}

export async function setStatsigUser(user: StatsigUser): Promise<void> {
  try {
    const client = getClient();
    if (!client) return;
    await client.updateUserAsync(user);
  } catch (error: unknown) {
    console.error('[Statsig] Failed to update user', error);
  }
}

export function logScreenerLandingPageViewed(props: LandingPageViewedProps): void {
  safeLog('screener_landing_page_viewed', { ...props });
}

export function logScreenerCtaClicked(props: CtaClickedProps): void {
  safeLog('screener_cta_clicked', { ...props });
}

export function logScreenerRecordingStarted(props: RecordingStartedProps): void {
  safeLog('screener_recording_started', { ...props });
}

export function logScreenerRecordingCompleted(props: RecordingCompletedProps): void {
  safeLog('screener_recording_completed', { ...props });
}

export function logScreenerRecordingRetaken(props: RecordingRetakenProps): void {
  safeLog('screener_recording_retaken', { ...props });
}

export function logScreenerCompleted(props: ScreenerCompletedProps): void {
  safeLog('screener_completed', { ...props });
}

export function logScreenerAbandoned(props: ScreenerAbandonedProps): void {
  safeLog('screener_abandoned', { ...props });
}

export function logScreenerCameraError(props: CameraErrorProps): void {
  safeLog('screener_camera_error', { ...props });
}

export function logScreenerUploadError(props: UploadErrorProps): void {
  safeLog('screener_upload_error', { ...props });
}

export function logScreenerPageLoadError(props: PageLoadErrorProps): void {
  safeLog('screener_page_load_error', { ...props });
}

export function logScreenerPermission(props: PermissionProps): void {
  safeLog('screener_permission_result', { ...props });
}

// VI-368: take-selection events for the "Pick Your Answer" A/B experiment
export function logInterviewTake1Selected(props: BaseProps): void {
  safeLog('vi_interview_take1', { ...props });
}

export function logInterviewTake2Selected(props: BaseProps): void {
  safeLog('vi_interview_take2', { ...props });
}

export function logInterviewTake3Selected(props: BaseProps): void {
  safeLog('vi_interview_take3', { ...props });
}
